export const ARCHIVE_VISIBILITY = {
  ADMIN: 'admin',
  CUSTOMER: 'customer',
  PUBLIC: 'public',
};

export const ARCHIVE_MEDIA_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  OTHER: 'other',
};

const buildCapture = ({
  key,
  label,
  shortLabel,
  mediaType,
  angle = '',
  purpose = '',
  details = [],
  filenameLabel = '',
  visibility = ARCHIVE_VISIBILITY.CUSTOMER,
  required = false,
  adminTask = '',
}) => ({
  key,
  label,
  shortLabel: shortLabel || label,
  mediaType,
  angle,
  purpose,
  details,
  filenameLabel: filenameLabel || label,
  visibility,
  required,
  adminTask: adminTask || label,
});

const buildStageDefinition = ({
  stageKey,
  chapterNumber,
  chapterTitle,
  internalGoal,
  filenamePrefix,
  suggestedCaptures = [],
  adminCaptureChecklist = [],
}) => ({
  stageKey,
  chapterNumber,
  chapterTitle,
  internalGoal,
  filenamePrefix,
  suggestedCaptures,
  adminCaptureChecklist,
});

export const PROJECT_STAGE_ARCHIVE_DEFINITIONS = {
  discoveryDesign: buildStageDefinition({
    stageKey: 'discoveryDesign',
    chapterNumber: 1,
    chapterTitle: 'Discovery & Design',
    filenamePrefix: 'ch01-discovery-design',
    internalGoal:
      'Document the beginning of the player story, the vision, and the emotional / sonic brief that will guide the rest of the build.',
    suggestedCaptures: [
      buildCapture({
        key: 'consultation-summary-doc',
        label: 'Consultation Summary Document',
        shortLabel: 'Consult summary',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the agreed direction, goals, and major takeaways.',
        details: [
          'Final written summary of the consultation',
          'Core sonic goals',
          'What is missing in the player’s current setup',
          'Non-negotiables and red flags',
        ],
        filenameLabel: 'consultation-summary',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'reference-inspiration-board',
        label: 'Reference / Inspiration Board',
        shortLabel: 'Inspiration board',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Flat lay or clean collage layout',
        purpose: 'Show the aesthetic and emotional direction behind the build.',
        details: [
          'Wood references',
          'Finish references',
          'Hardware references',
          'Mood-setting visual references',
        ],
        filenameLabel: 'inspiration-board',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'build-intent-notes',
        label: 'Internal Build Intent Notes',
        shortLabel: 'Intent notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture your internal interpretation of the player and the build.',
        details: [
          'How you translated the player’s language',
          'What you are listening for in the final drum',
          'What this build must not become',
        ],
        filenameLabel: 'build-intent-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
      buildCapture({
        key: 'initial-spec-sheet',
        label: 'Initial Spec Sheet',
        shortLabel: 'Spec draft',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the first formal draft of size, shell, finish, and hardware direction.',
        details: [
          'Diameter and depth',
          'Lug count direction',
          'Construction direction',
          'Material direction',
        ],
        filenameLabel: 'initial-spec-sheet',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
    ],
    adminCaptureChecklist: [
      'Write a clean consultation summary',
      'Record the emotional and sonic target in plain language',
      'Store reference images or inspiration board',
      'Record any hard non-negotiables',
      'Record any major contradictions or caution areas',
    ],
  }),

  commitmentPortal: buildStageDefinition({
    stageKey: 'commitmentPortal',
    chapterNumber: 2,
    chapterTitle: 'Commitment & Portal Setup',
    filenamePrefix: 'ch02-commitment-portal',
    internalGoal:
      'Document the moment the project becomes real: approval, commitment, scheduling, and portal setup.',
    suggestedCaptures: [
      buildCapture({
        key: 'approved-proposal-doc',
        label: 'Approved Proposal / Finalized Scope',
        shortLabel: 'Approved proposal',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the approved build scope and commitment point.',
        details: [
          'Approved proposal',
          'Finalized direction',
          'Timeline expectations',
          'Commitment details',
        ],
        filenameLabel: 'approved-proposal',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'portal-welcome-screen',
        label: 'Portal Welcome Screenshot',
        shortLabel: 'Portal welcome',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Clean screenshot or polished portal preview',
        purpose: 'Mark the beginning of the customer’s portal journey.',
        details: [
          'Customer-facing project portal activated',
          'Visible chapter structure',
          'Initial progress state',
        ],
        filenameLabel: 'portal-welcome',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'payment-confirmation-doc',
        label: 'Payment / Deposit Confirmation',
        shortLabel: 'Payment confirmation',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve payment confirmation or commitment documentation.',
        details: [
          'Deposit confirmation',
          'Receipt or invoice reference',
          'Any billing notes that matter to the project',
        ],
        filenameLabel: 'payment-confirmation',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
      buildCapture({
        key: 'internal-work-order',
        label: 'Internal Work Order Snapshot',
        shortLabel: 'Work order',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the internal project snapshot that drives the build.',
        details: [
          'Internal work order',
          'Version-locked spec snapshot',
          'Production notes',
        ],
        filenameLabel: 'internal-work-order',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Save approved proposal',
      'Save payment / deposit confirmation',
      'Initialize portal successfully',
      'Verify customer access to the correct project',
      'Create internal work order snapshot',
    ],
  }),

  woodVisionLockIn: buildStageDefinition({
    stageKey: 'woodVisionLockIn',
    chapterNumber: 3,
    chapterTitle: 'Wood & Vision Lock-In',
    filenamePrefix: 'ch03-wood-vision-lock-in',
    internalGoal:
      'Document the exact material and visual direction the drum will be built around.',
    suggestedCaptures: [
      buildCapture({
        key: 'wood-selection-hero-photo',
        label: 'Wood Selection Hero Photo',
        shortLabel: 'Wood hero',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Top-down or 45° workshop beauty shot of selected wood',
        purpose: 'Show the actual wood chosen for the build.',
        details: [
          'Best representative boards or staves',
          'Natural figure visible',
          'Clean lighting with true color',
        ],
        filenameLabel: 'wood-selection-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'veneer-reference-photo',
        label: 'Veneer / Figure Reference Photo',
        shortLabel: 'Veneer reference',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Flat, color-accurate reference shot',
        purpose: 'Capture the exact figure or veneer direction being used.',
        details: [
          'Exact veneer reference',
          'True color and grain',
          'No dramatic color grading',
        ],
        filenameLabel: 'veneer-reference',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'material-approval-doc',
        label: 'Material Approval Summary',
        shortLabel: 'Material approval',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture what was chosen and why.',
        details: [
          'Species chosen',
          'Why it supports the build',
          'Visual direction',
          'Accent direction',
        ],
        filenameLabel: 'material-approval-summary',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'shop-notes-material-eval',
        label: 'Internal Material Evaluation Notes',
        shortLabel: 'Material eval',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture your internal notes about the wood temperament and risks.',
        details: [
          'Movement concerns',
          'Grain behavior',
          'Structural observations',
          'Why certain boards were rejected',
        ],
        filenameLabel: 'material-evaluation-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Photograph selected wood clearly',
      'Save veneer reference image',
      'Record the rationale for chosen material',
      'Capture internal material behavior notes',
      'Confirm final visual direction before cutting',
    ],
  }),

  rawShellCreation: buildStageDefinition({
    stageKey: 'rawShellCreation',
    chapterNumber: 4,
    chapterTitle: 'Raw Shell Creation',
    filenamePrefix: 'ch04-raw-shell-creation',
    internalGoal:
      'Document the physical birth of the shell from raw material into real structure.',
    suggestedCaptures: [
      buildCapture({
        key: 'milled-staves-photo',
        label: 'Milled Staves / Prepared Parts Photo',
        shortLabel: 'Prepared parts',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Top-down bench shot or 45° parts lineup shot',
        purpose: 'Show the material after prep and before glue-up.',
        details: [
          'Clean arrangement',
          'Visible consistency of cut pieces',
          'Shop realism without clutter overload',
        ],
        filenameLabel: 'milled-staves',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'dry-fit-shell-photo',
        label: 'Dry Fit Shell Photo',
        shortLabel: 'Dry fit',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Side angle showing ring shape and seam alignment',
        purpose: 'Show the shell taking shape before glue.',
        details: [
          'Dry-fit assembly',
          'Visible geometry',
          'Early shell personality',
        ],
        filenameLabel: 'dry-fit-shell',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'glue-up-video',
        label: 'Glue-Up / Compression Video',
        shortLabel: 'Glue-up video',
        mediaType: ARCHIVE_MEDIA_TYPE.VIDEO,
        angle: 'Short side-angle or 3/4 workshop video of the clamp-up process',
        purpose: 'Capture the moment the shell is truly born.',
        details: [
          'Compression in progress',
          'Clamp action or shell closing',
          'Short cinematic clip, not a long explanation',
        ],
        filenameLabel: 'glue-up-compression',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'raw-shell-hero-photo',
        label: 'Raw Shell Hero Photo',
        shortLabel: 'Raw shell hero',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Dramatic 3/4 shell portrait on dark bench or dark background',
        purpose: 'Document the first unmistakable shell form.',
        details: [
          'Raw shell only',
          'No hardware',
          'Strong workshop atmosphere',
        ],
        filenameLabel: 'raw-shell-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'raw-shell-measurements-doc',
        label: 'Raw Shell Geometry Notes',
        shortLabel: 'Geometry notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the shell geometry and any corrective notes.',
        details: [
          'Roundness notes',
          'Fit observations',
          'Clamp behavior',
          'Corrections made',
        ],
        filenameLabel: 'raw-shell-geometry-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Photograph milled parts',
      'Photograph dry fit shell',
      'Capture short glue-up or compression clip',
      'Photograph raw shell after glue-up',
      'Record geometry / correction notes internally',
    ],
  }),

  shellTrueingTorchTune: buildStageDefinition({
    stageKey: 'shellTrueingTorchTune',
    chapterNumber: 5,
    chapterTitle: 'Shell Trueing & Torch Tune',
    filenamePrefix: 'ch05-shell-trueing-torch-tune',
    internalGoal:
      'Document the refinement of shell geometry and the moment the shell begins to reveal its deeper character.',
    suggestedCaptures: [
      buildCapture({
        key: 'trueing-process-photo',
        label: 'Shell Trueing Process Photo',
        shortLabel: 'Trueing photo',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Side angle showing shell on jig, lathe, or truing setup',
        purpose: 'Show the shell being refined into truth and consistency.',
        details: [
          'Process in motion or setup',
          'Visible shell refinement stage',
        ],
        filenameLabel: 'shell-trueing-process',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'torch-tune-video',
        label: 'Torch Tune Video',
        shortLabel: 'Torch tune',
        mediaType: ARCHIVE_MEDIA_TYPE.VIDEO,
        angle: 'Tight cinematic clip with controlled side / 3/4 angle',
        purpose: 'Capture a dramatic and meaningful moment in the shell refinement process.',
        details: [
          'Short, visually strong clip',
          'Focus on shell and flame interaction',
          'Dark, controlled environment if possible',
        ],
        filenameLabel: 'torch-tune-process',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'refined-shell-hero-photo',
        label: 'Refined Shell Hero Photo',
        shortLabel: 'Refined shell',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Clean 3/4 beauty shot after trueing and prep',
        purpose: 'Show the shell after refinement and before finish details take over.',
        details: [
          'Visible smoothness',
          'Improved presence',
          'Workshop elegance',
        ],
        filenameLabel: 'refined-shell-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'shell-trueing-measurements-doc',
        label: 'Shell Trueing Measurements / Notes',
        shortLabel: 'Trueing notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the geometry and refinement notes.',
        details: [
          'Flatness checks',
          'Thickness notes',
          'Movement after torch tune',
          'Corrective actions',
        ],
        filenameLabel: 'shell-trueing-measurements',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Capture shell on trueing setup',
      'Capture short torch tune clip if performed',
      'Photograph refined shell cleanly',
      'Record geometry and measurement notes',
      'Record any shell behavior observations',
    ],
  }),

  exteriorArtFinish: buildStageDefinition({
    stageKey: 'exteriorArtFinish',
    chapterNumber: 6,
    chapterTitle: 'Exterior Art & Finish',
    filenamePrefix: 'ch06-exterior-art-finish',
    internalGoal:
      'Document the visual transformation of the shell into a finished artistic instrument.',
    suggestedCaptures: [
      buildCapture({
        key: 'pre-finish-shell-photo',
        label: 'Pre-Finish Shell Photo',
        shortLabel: 'Pre-finish',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Neutral 3/4 shell portrait before finish layers',
        purpose: 'Create a before/after story moment.',
        details: [
          'Shell before final finish transformation',
          'Color-accurate baseline shot',
        ],
        filenameLabel: 'pre-finish-shell',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'finish-process-photo',
        label: 'Finish Process Photo',
        shortLabel: 'Finish process',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Close detail or mid-process bench shot',
        purpose: 'Show that the finish is handcrafted, not generic.',
        details: [
          'Finish application detail',
          'Resin/accent detail',
          'Visible craftsmanship',
        ],
        filenameLabel: 'finish-process',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'finish-detail-macro-photo',
        label: 'Finish Detail Macro Photo',
        shortLabel: 'Macro detail',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Macro close-up showing grain, sheen, and accent behavior',
        purpose: 'Capture the premium detail work up close.',
        details: [
          'Wood figure clarity',
          'Accent integration',
          'Surface depth',
        ],
        filenameLabel: 'finish-detail-macro',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'finished-shell-hero-photo',
        label: 'Finished Shell Hero Photo',
        shortLabel: 'Finished shell hero',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Cinematic 3/4 shell portrait on dark background',
        purpose: 'Document the shell at its first truly finished visual state.',
        details: [
          'No hardware yet if possible',
          'Luxury lighting',
          'High drama, high accuracy',
        ],
        filenameLabel: 'finished-shell-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'finish-system-notes-doc',
        label: 'Internal Finish System Notes',
        shortLabel: 'Finish notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture the finish system and any corrections or observations.',
        details: [
          'Finish stack',
          'Cure notes',
          'Correction passes',
          'Special handling notes',
        ],
        filenameLabel: 'finish-system-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Capture pre-finish shell baseline photo',
      'Capture finish-in-progress image',
      'Capture macro detail of grain / accent / sheen',
      'Capture finished shell hero shot',
      'Record internal finish notes',
    ],
  }),

  edgesSnareBeds: buildStageDefinition({
    stageKey: 'edgesSnareBeds',
    chapterNumber: 7,
    chapterTitle: 'Edges & Snare Beds',
    filenamePrefix: 'ch07-edges-snare-beds',
    internalGoal:
      'Document the precision detail work that turns the shell into a serious performance surface.',
    suggestedCaptures: [
      buildCapture({
        key: 'bearing-edge-detail-photo',
        label: 'Bearing Edge Detail Photo',
        shortLabel: 'Edge detail',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Macro or tight close-up from shell top edge',
        purpose: 'Show the precision of the edge work.',
        details: [
          'Sharpness and cleanliness',
          'Smooth profile',
          'Visible refinement',
        ],
        filenameLabel: 'bearing-edge-detail',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'snare-bed-detail-photo',
        label: 'Snare Bed Detail Photo',
        shortLabel: 'Snare bed detail',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Tight inside-shell angle focused on the bed area',
        purpose: 'Show the subtle but critical shaping of the snare bed.',
        details: [
          'Smooth transition',
          'Precise shaping',
          'Document hidden craftsmanship',
        ],
        filenameLabel: 'snare-bed-detail',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'head-fit-test-photo',
        label: 'Head Fit / Seating Test Photo',
        shortLabel: 'Head fit',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Top-down or slight angle with head seated on shell',
        purpose: 'Show the shell is beginning to function as a true instrument.',
        details: [
          'Clean fit',
          'No awkward binding',
          'Visible precision',
        ],
        filenameLabel: 'head-fit-test',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'edge-bed-qc-notes-doc',
        label: 'Edge / Bed QC Notes',
        shortLabel: 'Edge QC notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture measurements and internal quality checks.',
        details: [
          'Edge notes',
          'Snare bed notes',
          'Head seating observations',
          'Any final corrective notes',
        ],
        filenameLabel: 'edge-bed-qc-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Photograph bearing edge detail clearly',
      'Photograph snare bed detail clearly',
      'Capture head seating / fit test image',
      'Record QC notes on edges and beds',
    ],
  }),

  hardwareAssembly: buildStageDefinition({
    stageKey: 'hardwareAssembly',
    chapterNumber: 8,
    chapterTitle: 'Hardware & Assembly',
    filenamePrefix: 'ch08-hardware-assembly',
    internalGoal:
      'Document the shell becoming a full instrument through accurate and intentional assembly.',
    suggestedCaptures: [
      buildCapture({
        key: 'hardware-layout-photo',
        label: 'Hardware Layout Photo',
        shortLabel: 'Hardware layout',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Top-down organized parts layout',
        purpose: 'Show the parts that will complete the instrument.',
        details: [
          'Lugs',
          'Hoops',
          'Throw-off',
          'Badge',
          'Hardware finish clearly visible',
        ],
        filenameLabel: 'hardware-layout',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'assembly-in-progress-photo',
        label: 'Assembly In Progress Photo',
        shortLabel: 'Assembly progress',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: '3/4 bench shot during install',
        purpose: 'Show the transformation from shell to full drum.',
        details: [
          'Hardware going on',
          'Visible process',
          'Workshop realism',
        ],
        filenameLabel: 'assembly-in-progress',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'assembled-drum-hero-photo',
        label: 'Assembled Drum Hero Photo',
        shortLabel: 'Assembled hero',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Cinematic 3/4 product shot of assembled drum',
        purpose: 'Capture the first complete visual of the drum as an instrument.',
        details: [
          'Badge visible',
          'Hardware visible',
          'Finished shell visible',
          'Strong dark-background presentation',
        ],
        filenameLabel: 'assembled-drum-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'hardware-detail-macro-photo',
        label: 'Hardware Detail Macro Photo',
        shortLabel: 'Hardware macro',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Macro close-up of lug, hoop, badge, or throw detail',
        purpose: 'Show premium execution in the small details.',
        details: [
          'Finish quality',
          'Fit and alignment',
          'Luxury detail',
        ],
        filenameLabel: 'hardware-detail-macro',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'assembly-qc-notes-doc',
        label: 'Assembly QC Notes',
        shortLabel: 'Assembly QC',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture internal notes about fit, alignment, and any corrections.',
        details: [
          'Hardware layout notes',
          'Install corrections',
          'Alignment notes',
          'Functional checks',
        ],
        filenameLabel: 'assembly-qc-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Photograph hardware layout cleanly',
      'Capture assembly-in-progress image',
      'Capture first full assembled hero shot',
      'Capture at least one macro hardware detail',
      'Record assembly QC notes',
    ],
  }),

  legacyTuningMedia: buildStageDefinition({
    stageKey: 'legacyTuningMedia',
    chapterNumber: 9,
    chapterTitle: 'Legacy Tuning & Media',
    filenamePrefix: 'ch09-legacy-tuning-media',
    internalGoal:
      'Document the drum’s actual voice and the moment the build becomes emotionally complete.',
    suggestedCaptures: [
      buildCapture({
        key: 'final-drum-hero-photo',
        label: 'Final Drum Hero Photo',
        shortLabel: 'Final hero',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Best cinematic 3/4 hero angle with full drum visible',
        purpose: 'Create the definitive visual image of the finished drum.',
        details: [
          'Premium hero shot',
          'Dark background',
          'Badge and shell visible',
          'Lighting worthy of final reveal',
        ],
        filenameLabel: 'final-drum-hero',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'final-drum-detail-gallery',
        label: 'Final Detail Gallery Images',
        shortLabel: 'Detail gallery',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Macro and close product details',
        purpose: 'Capture multiple premium finished details.',
        details: [
          'Badge detail',
          'Shell detail',
          'Hoop/lug detail',
          'Throw-off detail',
        ],
        filenameLabel: 'final-detail-gallery',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'final-sound-sample-audio',
        label: 'Final Sound Sample Audio',
        shortLabel: 'Sound sample',
        mediaType: ARCHIVE_MEDIA_TYPE.AUDIO,
        purpose: 'Let the drum speak for itself.',
        details: [
          'Clean audio sample',
          'Representative tuning',
          'No talking required',
          'Useful to the owner and meaningful to the story',
        ],
        filenameLabel: 'final-sound-sample',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'tuning-notes-doc',
        label: 'Final Tuning Notes',
        shortLabel: 'Tuning notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve useful tuning guidance and final shell behavior notes.',
        details: [
          'Sweet spot notes',
          'Head / wire notes',
          'Response notes',
          'Future service reference',
        ],
        filenameLabel: 'final-tuning-notes',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'legacy-capture-internal-notes',
        label: 'Internal Legacy Capture Notes',
        shortLabel: 'Legacy notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture your internal take on how the drum landed.',
        details: [
          'How the build delivered',
          'Any surprises',
          'What the shell wanted',
          'What you learned from the build',
        ],
        filenameLabel: 'legacy-capture-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Capture final hero photo',
      'Capture final detail shots',
      'Record final sound sample audio',
      'Save final tuning notes',
      'Record internal legacy capture notes',
    ],
  }),

  finalQAPackagingDelivery: buildStageDefinition({
    stageKey: 'finalQAPackagingDelivery',
    chapterNumber: 10,
    chapterTitle: 'Final QA, Packaging & Delivery',
    filenamePrefix: 'ch10-final-qa-packaging-delivery',
    internalGoal:
      'Document the final verification, protection, and handoff of the drum so the story closes with the same care it was built with.',
    suggestedCaptures: [
      buildCapture({
        key: 'final-qa-signoff-doc',
        label: 'Final QA Sign-Off Document',
        shortLabel: 'QA sign-off',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve the final quality verification before the drum leaves the shop.',
        details: [
          'Final function check',
          'Final cosmetic check',
          'Final sound check',
          'Sign-off that the instrument is ready to leave',
        ],
        filenameLabel: 'final-qa-signoff',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
        required: true,
      }),
      buildCapture({
        key: 'packaging-photo',
        label: 'Packaging Protection Photo',
        shortLabel: 'Packaging',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Top-down or 3/4 shot of packed drum before box closure',
        purpose: 'Document the care taken to protect the instrument for transit.',
        details: [
          'Shell protected',
          'Movement controlled',
          'Premium presentation',
          'Useful reference in case of transit issues',
        ],
        filenameLabel: 'packaging-protection',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'shipment-confirmation-doc',
        label: 'Shipment / Tracking Confirmation',
        shortLabel: 'Shipment',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Preserve delivery confirmation and tracking details.',
        details: [
          'Tracking number',
          'Carrier used',
          'Shipment date',
          'Delivery notes if needed',
        ],
        filenameLabel: 'shipment-confirmation',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
        required: true,
      }),
      buildCapture({
        key: 'delivered-drum-photo',
        label: 'Final Packed Drum Beauty Shot',
        shortLabel: 'Packed beauty',
        mediaType: ARCHIVE_MEDIA_TYPE.IMAGE,
        angle: 'Clean final shot before shipment or handoff',
        purpose: 'Provide a dignified visual closing image for the build story.',
        details: [
          'Final protected presentation',
          'Emotionally strong closing image',
          'Can serve as final chapter image',
        ],
        filenameLabel: 'final-packed-drum-beauty',
        visibility: ARCHIVE_VISIBILITY.CUSTOMER,
      }),
      buildCapture({
        key: 'delivery-closeout-notes',
        label: 'Delivery Closeout Notes',
        shortLabel: 'Closeout notes',
        mediaType: ARCHIVE_MEDIA_TYPE.DOCUMENT,
        purpose: 'Capture any final internal notes about shipment, handoff, or follow-up.',
        details: [
          'Shipping notes',
          'Customer handoff notes',
          'Anything to follow up on after arrival',
        ],
        filenameLabel: 'delivery-closeout-notes',
        visibility: ARCHIVE_VISIBILITY.ADMIN,
      }),
    ],
    adminCaptureChecklist: [
      'Complete final QA sign-off',
      'Capture protective packaging photo',
      'Save shipment / tracking confirmation',
      'Capture final packed-drum closing image',
      'Record internal closeout notes',
    ],
  }),
};

export const PROJECT_STAGE_ARCHIVE_MAP = Object.values(
  PROJECT_STAGE_ARCHIVE_DEFINITIONS
).reduce((acc, stage) => {
  acc[stage.stageKey] = stage;
  return acc;
}, {});

export const getStageArchiveDefinition = (stageKey) =>
  PROJECT_STAGE_ARCHIVE_DEFINITIONS[stageKey] || null;

export const getStageSuggestedCaptures = (stageKey) =>
  PROJECT_STAGE_ARCHIVE_DEFINITIONS[stageKey]?.suggestedCaptures || [];

export const getStageAdminCaptureChecklist = (stageKey) =>
  PROJECT_STAGE_ARCHIVE_DEFINITIONS[stageKey]?.adminCaptureChecklist || [];

export const getArchiveCaptureByKey = (stageKey, captureKey) => {
  const captures = getStageSuggestedCaptures(stageKey);
  return captures.find((capture) => capture.key === captureKey) || null;
};